import React, { useState, Fragment, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Dialog, Transition } from '@headlessui/react'
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { User, Video, Globe, ArrowRight, ChevronRight } from 'lucide-react'
import Logo from "../assets/logo.webp";

const solutionsItems = [
  {
    name: 'Vupop for Fans',
    href: '/VupopForFans',
    icon: User,
  },
  {
    name: 'Vupop for Broadcasters',
    href: '/VupopForBroadcasters',
    icon: Video,
  },
  {
    name: 'Vupop for Brands',
    href: '/VupopForBrands',
    icon: Globe,
  },
]

const navigation = [
  { name: 'Solutions', href: '#', hasMegaMenu: true },
  { name: 'Pricing', href: "https://manager.vupop.io/broadcaster/signup/subscription" },
  { name: 'About Us', href: '#about-us' },
  { name: 'Contact', href: '/ContactUs' },
  { name: 'Blogs', href: '/blogs' },
  { name: 'Documentation', href: '/docs' },
]

const mobileNavigation = [
  { name: 'About Us', href: '#about-us' },
  { name: 'Features', href: '#features' },
  { name: 'Contact Us', href: '/ContactUs' },
  { name: 'Pricing', href: "https://manager.vupop.io/broadcaster/signup/subscription" },
  { name: 'Documentation', href: '/docs' },
  { name: 'Vupop for Fans', href: '/VupopForFans' },
  { name: 'Vupop for Broadcasters', href: '/VupopForBroadcasters' },
  { name: 'Vupop for Brands', href: '/VupopForBrands' },
]

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [solutionsOpen, setSolutionsOpen] = useState(false)
  const [hoveredSolution, setHoveredSolution] = useState(null)
  const solutionsRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (solutionsRef.current && !solutionsRef.current.contains(event.target)) {
        setSolutionsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNavClick = (e, href) => {
    if (href === '#' || href.startsWith('http') || !href.startsWith('#')) return
    e.preventDefault()
    const targetId = href.replace('#', '')
    if (location.pathname === '/') {
      const el = document.getElementById(targetId)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      sessionStorage.setItem('scrollTarget', targetId)
      navigate('/')
    }
  }

  const handleSolutionClick = (href) => {
    setSolutionsOpen(false)
    navigate(href)
  }

  return (
    <header className="mt-8 z-50 relative px-4 sm:px-6 lg:px-8">
      <nav
        className="mx-auto flex max-w-6xl bg-[#181A1D] px-5 py-3 sm:py-3.5 rounded-xl items-center justify-between relative"
        aria-label="Global"
      >
        {/* Logo */}
        <div className="flex-shrink-0">
          <a href="/" className="block">
            <img
              src={Logo}
              alt="Vupop logo"
              className="h-7 sm:h-8 w-auto"
            />
          </a>
        </div>

        {/* Desktop Nav Links — centered */}
        <div className="flex items-center gap-x-8 lg:hidden">
          {navigation.map((item) =>
            item.hasMegaMenu ? (
              <div
                key={item.name}
                ref={solutionsRef}
                className="relative"
                onMouseEnter={() => setSolutionsOpen(true)}
                onMouseLeave={() => setSolutionsOpen(false)}
              >
                <button
                  className={`flex items-center gap-1 text-sm font-medium tracking-wide transition-colors ${
                    solutionsOpen ? 'text-[#FFF528]' : 'text-white hover:text-[#FFF528]'
                  }`}
                >
                  {item.name}
                  <ChevronDownIcon
                    className={`h-3.5 w-3.5 transition-transform duration-200 ${solutionsOpen ? 'rotate-180 text-[#FFF528]' : ''}`}
                  />
                </button>

                {/* Dropdown */}
                <Transition
                  show={solutionsOpen}
                  as={Fragment}
                  enter="transition ease-out duration-150"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <div className="absolute left-0 top-full mt-3 w-64 rounded-xl bg-[#15171A] shadow-2xl border border-white/5 overflow-hidden">
                    <div className="py-2">
                      {solutionsItems.map((sol) => {
                        const Icon = sol.icon
                        const isHovered = hoveredSolution === sol.name
                        return (
                          <button
                            key={sol.name}
                            onClick={() => handleSolutionClick(sol.href)}
                            onMouseEnter={() => setHoveredSolution(sol.name)}
                            onMouseLeave={() => setHoveredSolution(null)}
                            className="w-full flex items-center justify-between px-4 py-3.5 text-left group hover:bg-white/5 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <Icon
                                className={`h-5 w-5 transition-colors ${isHovered ? 'text-[#FFF528]' : 'text-white/60'}`}
                                strokeWidth={1.5}
                              />
                              <span className={`text-sm font-medium transition-colors ${isHovered ? 'text-white' : 'text-white/80'}`}>
                                {sol.name}
                              </span>
                            </div>
                            <ArrowRight
                              className={`h-4 w-4 text-[#FFF528] transition-all duration-200 ${
                                isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
                              }`}
                            />
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </Transition>
              </div>
            ) : (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="text-sm font-medium tracking-wide text-white hover:text-[#FFF528] transition-colors"
              >
                {item.name}
              </a>
            )
          )}
        </div>

        {/* Sign Up button — desktop */}
        <div className="flex items-center lg:hidden">
          <a
            href="https://manager.vupop.io/broadcaster/login"
            className="rounded-md bg-[#FFF528] px-6 py-2.5 text-xs font-black uppercase tracking-widest text-black hover:bg-yellow-300 transition-colors"
            style={{ fontFamily: 'Norwester, sans-serif' }}
          >
            Sign Up
          </a>
        </div>

        {/* Hamburger — mobile/tablet */}
        <div className="hidden lg:flex">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2 p-2 text-white"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:flex" onClose={setMobileMenuOpen}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/80" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="relative w-full max-w-sm bg-[#15171A] p-6 shadow-xl flex flex-col">
                {/* Drawer header */}
                <div className="flex items-center justify-between mb-10">
                  <a href="/" className="block">
                    <img src={Logo} alt="logo" className="h-7 w-auto" />
                  </a>
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(false)}
                    className="-m-2 p-2 text-white hover:text-[#FFF528] transition-colors"
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                {/* Drawer nav items */}
                <div className="flex flex-col items-center gap-y-6 text-center">
                  {mobileNavigation.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      onClick={(e) => {
                        handleNavClick(e, item.href)
                        setMobileMenuOpen(false)
                      }}
                      className="text-sm font-bold uppercase tracking-widest text-white hover:text-[#FFF528] transition-colors"
                      style={{ fontFamily: 'Norwester, sans-serif' }}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>

                {/* Sign Up */}
                <div className="mt-auto pt-10">
                  <hr className="border-white/10 mb-8" />
                  <a
                    href="https://manager.vupop.io/broadcaster/login"
                    className="block w-full text-center rounded-lg bg-[#FFF528] py-3 text-sm font-black uppercase tracking-widest text-black hover:bg-yellow-300 transition-colors"
                    style={{ fontFamily: 'Norwester, sans-serif' }}
                  >
                    Sign Up
                  </a>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </header>
  )
}